import {
  collection,
  query,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  where,
  orderBy,
  limit,
  Timestamp,
  QueryConstraint,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';

import { db } from './config';
import type { Product } from '@shared/types/product';
import { ProductCategory } from '@shared/types/product';

/**
 * Convert Firestore timestamp to Date
 */
function timestampToDate(timestamp: Timestamp | Date | null | undefined): Date {
  if (!timestamp) return new Date();
  if (timestamp instanceof Date) return timestamp;
  return timestamp.toDate();
}

/**
 * Get all products
 */
export async function getProducts(limitCount?: number): Promise<Product[]> {
  const constraints: QueryConstraint[] = [orderBy('createdAt', 'desc')];
  if (limitCount) {
    constraints.push(limit(limitCount));
  }

  const productsQuery = query(collection(db, 'products'), ...constraints);
  const snapshot = await getDocs(productsQuery);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name || '',
      description: data.description || '',
      category: data.category || ProductCategory.ACCESSORY,
      price: data.price || 0,
      compareAtPrice: data.compareAtPrice || undefined,
      sku: data.sku || undefined,
      stock: data.stock || 0,
      inStock: data.inStock !== undefined ? data.inStock : data.stock > 0,
      imageUrls: data.imageUrls || [],
      specifications: data.specifications || undefined,
      tags: data.tags || undefined,
      isFeatured: data.isFeatured || false,
      isActive: data.isActive !== undefined ? data.isActive : true,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    };
  });
}

/**
 * Get products by category
 */
export async function getProductsByCategory(
  category: ProductCategory,
  limitCount?: number
): Promise<Product[]> {
  const constraints: QueryConstraint[] = [
    where('category', '==', category),
    where('isActive', '==', true),
    orderBy('createdAt', 'desc'),
  ];
  if (limitCount) {
    constraints.push(limit(limitCount));
  }

  const productsQuery = query(collection(db, 'products'), ...constraints);
  const snapshot = await getDocs(productsQuery);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name || '',
      description: data.description || '',
      category: data.category || ProductCategory.ACCESSORY,
      price: data.price || 0,
      compareAtPrice: data.compareAtPrice || undefined,
      sku: data.sku || undefined,
      stock: data.stock || 0,
      inStock: data.inStock !== undefined ? data.inStock : data.stock > 0,
      imageUrls: data.imageUrls || [],
      specifications: data.specifications || undefined,
      tags: data.tags || undefined,
      isFeatured: data.isFeatured || false,
      isActive: data.isActive !== undefined ? data.isActive : true,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    };
  });
}

/**
 * Get featured products
 */
export async function getFeaturedProducts(limitCount: number = 8): Promise<Product[]> {
  const productsQuery = query(
    collection(db, 'products'),
    where('isFeatured', '==', true),
    where('isActive', '==', true),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(productsQuery);

  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      name: data.name || '',
      description: data.description || '',
      category: data.category || ProductCategory.ACCESSORY,
      price: data.price || 0,
      compareAtPrice: data.compareAtPrice || undefined,
      sku: data.sku || undefined,
      stock: data.stock || 0,
      inStock: data.inStock !== undefined ? data.inStock : data.stock > 0,
      imageUrls: data.imageUrls || [],
      specifications: data.specifications || undefined,
      tags: data.tags || undefined,
      isFeatured: data.isFeatured || false,
      isActive: data.isActive !== undefined ? data.isActive : true,
      createdAt: timestampToDate(data.createdAt),
      updatedAt: timestampToDate(data.updatedAt),
    };
  });
}

/**
 * Get a single product by ID
 */
export async function getProduct(productId: string): Promise<Product | null> {
  const productDoc = await getDoc(doc(db, 'products', productId));

  if (!productDoc.exists()) {
    return null;
  }

  const data = productDoc.data();
  return {
    id: productDoc.id,
    name: data.name || '',
    description: data.description || '',
    category: data.category || ProductCategory.ACCESSORY,
    price: data.price || 0,
    compareAtPrice: data.compareAtPrice || undefined,
    sku: data.sku || undefined,
    stock: data.stock || 0,
    inStock: data.inStock !== undefined ? data.inStock : data.stock > 0,
    imageUrls: data.imageUrls || [],
    specifications: data.specifications || undefined,
    tags: data.tags || undefined,
    isFeatured: data.isFeatured || false,
    isActive: data.isActive !== undefined ? data.isActive : true,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

/**
 * Create a single product
 */
export async function createProduct(productData: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  const docRef = await addDoc(collection(db, 'products'), {
    ...productData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Create multiple products in batch
 * Firestore batch limit is 500 operations, so we'll process in chunks
 */
export async function createProductsBatch(
  products: Array<Omit<Product, 'id' | 'createdAt' | 'updatedAt'>>
): Promise<{ success: number; failed: number; errors: Array<{ index: number; error: string }> }> {
  const BATCH_SIZE = 500;
  let success = 0;
  let failed = 0;
  const errors: Array<{ index: number; error: string }> = [];

  for (let i = 0; i < products.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = products.slice(i, i + BATCH_SIZE);

    chunk.forEach((product, chunkIndex) => {
      const docRef = doc(collection(db, 'products'));
      try {
        batch.set(docRef, {
          ...product,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      } catch (error) {
        const globalIndex = i + chunkIndex;
        errors.push({ index: globalIndex, error: error instanceof Error ? error.message : 'Unknown error' });
        failed++;
      }
    });

    try {
      await batch.commit();
      success += chunk.length;
    } catch (error) {
      // If batch fails, mark all items in chunk as failed
      chunk.forEach((_, chunkIndex) => {
        const globalIndex = i + chunkIndex;
        errors.push({
          index: globalIndex,
          error: error instanceof Error ? error.message : 'Batch commit failed',
        });
        failed++;
      });
    }
  }

  return { success, failed, errors };
}

/**
 * Update a product
 */
export async function updateProduct(productId: string, updates: Partial<Product>): Promise<void> {
  const docRef = doc(db, 'products', productId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a product
 */
export async function deleteProduct(productId: string): Promise<void> {
  const docRef = doc(db, 'products', productId);
  await updateDoc(docRef, {
    isActive: false,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Search products by name or description
 */
export async function searchProducts(searchQuery: string): Promise<Product[]> {
  // Note: Firestore doesn't support full-text search natively
  // For production, consider using Algolia or similar
  // This is a simple implementation that filters client-side
  const allProducts = await getProducts();
  const queryLower = searchQuery.toLowerCase();

  return allProducts.filter(
    (product) =>
      product.name.toLowerCase().includes(queryLower) ||
      product.description.toLowerCase().includes(queryLower) ||
      product.tags?.some((tag) => tag.toLowerCase().includes(queryLower))
  );
}

/**
 * Check if SKU already exists
 */
export async function skuExists(sku: string): Promise<boolean> {
  const snapshot = await getDocs(query(collection(db, 'products'), where('sku', '==', sku), limit(1)));
  return !snapshot.empty;
}
